import React from "react";
import { Navigate } from "react-router-dom";
import { getRole } from "../common/function";
import { PROFILE_ROUTE } from "../constants/RoutePaths";
import { useFeatureFlags } from "../context/FeatureFlagContext";

/**
 * Route guard that restricts access to Dashboard and Invoices for Supplier users.
 *
 * Role-based restriction: Suppliers/Sub Suppliers are normally redirected to
 * Profile. The supplier portal is disabled by default — suppliers can only
 * access Profile (for ACH entry).
 *
 * Per-team override: when `supplierPortalEnabled` is set on the supplier's own
 * team in `tblteam.feature_flags`, the redirect is bypassed and the supplier
 * sees the same routes as non-supplier users (with their own data scope).
 * This allows enabling the broader supplier portal experience on a per-tenant
 * basis without changing the global default. Default remains false everywhere
 * to preserve the existing role-based lockdown for HACOV PROD and others.
 *
 * See: hacov/shared/docs/SUPPLIER_PORTAL_ROLE_BASED_RESTRICTION.md
 */
const SupplierRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const isSupplier = ["Supplier", "Sub Supplier"].includes(getRole() ?? "");
  const { supplierPortalEnabled } = useFeatureFlags();

  // Suppliers are redirected unless the portal is explicitly enabled for their team
  if (isSupplier && !supplierPortalEnabled) {
    return <Navigate to={PROFILE_ROUTE} replace />;
  }

  // Non-suppliers always have access; suppliers with portal enabled fall through
  return children;
};

export default SupplierRouteGuard;
